import React from "react";
import { Globe, MapPin, Map, Mail, Building2, Hash, Clock, ShieldAlert, AlertTriangle } from "lucide-react";

// Risk level → Aurora token mapping (logic preserved, points at design tokens)
const LEVEL_COLORS = {
  critical: "var(--au-error)",
  high: "var(--au-error)",
  medium: "var(--au-warn)",
  low: "var(--au-accent-ink)"
};

export default function IPIntelligenceCard({ data }) {
  if (!data || data.error) return null;
  const { ip, geo, reputation, risk_flags = [] } = data;

  return (
    <div className="au-glass au-pad" style={{ marginBottom: 20 }}>
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 18,
        paddingBottom: 14,
        borderBottom: "1px solid var(--au-hair)"
      }}>
        <div className="au-eyebrow" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <Globe size={14} aria-hidden="true" /> IP INTELLIGENCE
        </div>
        <div className="au-chip au-chip-status au-mono" style={{ color: "var(--au-accent-ink)" }}>
          {ip || "—"}
        </div>
      </div>

      {geo && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14, marginBottom: 20 }}>
          <InfoCell icon={MapPin} label="Location" value={[geo.city, geo.region, geo.country].filter(Boolean).join(", ") || "Unknown"} />
          <InfoCell icon={Map} label="Coordinates" value={geo.latitude && geo.longitude ? `${parseFloat(geo.latitude).toFixed(4)}°, ${parseFloat(geo.longitude).toFixed(4)}°` : "—"} />
          <InfoCell icon={Mail} label="Postal Code" value={geo.postal || "—"} />
          <InfoCell icon={Building2} label="ISP / Org" value={geo.isp || "Unknown"} />
          <InfoCell icon={Hash} label="ASN" value={geo.asn || "N/A"} />
          <InfoCell icon={Clock} label="Timezone" value={geo.timezone || "—"} />
        </div>
      )}

      {reputation && !reputation.error && (
        <div style={{ marginBottom: 18 }}>
          <div style={{
            fontSize: 11, color: "var(--au-text-2)", letterSpacing: 1.5, fontWeight: 700, marginBottom: 10,
            display: "inline-flex", alignItems: "center", gap: 8, textTransform: "uppercase"
          }}>
            <ShieldAlert size={14} aria-hidden="true" /> THREAT REPUTATION INTELLIGENCE
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12 }}>
            <ScoreCell label="Abuse Score" value={(reputation.abuse_score || 0) + "%"}
              color={reputation.abuse_score >= 75 ? "var(--au-error)" : reputation.abuse_score >= 25 ? "var(--au-warn)" : "var(--au-success)"} />
            <ScoreCell label="Total Reports" value={reputation.total_reports || 0}
              color={reputation.total_reports > 50 ? "var(--au-error)" : reputation.total_reports > 5 ? "var(--au-warn)" : "var(--au-success)"} />
            <ScoreCell label="Usage Type" value={reputation.usage_type || "Unknown"} color="var(--au-accent-ink)" />
            <ScoreCell label="TOR Exit?" value={reputation.is_tor ? "YES" : "No"}
              color={reputation.is_tor ? "var(--au-error)" : "var(--au-success)"} />
          </div>
        </div>
      )}

      {reputation && reputation.error && (
        <div style={{
          background: "var(--au-glass)",
          border: "1px solid var(--au-hair)",
          borderRadius: "var(--au-r-sm)",
          padding: 10,
          color: "var(--au-warn)",
          fontSize: 12,
          marginBottom: 16,
          display: "flex",
          alignItems: "center",
          gap: 8
        }}>
          <AlertTriangle size={14} aria-hidden="true" /> {reputation.error}
        </div>
      )}

      {risk_flags.length > 0 && (
        <div>
          <div style={{
            fontSize: 11, color: "var(--au-text-2)", letterSpacing: 1.5, fontWeight: 700, marginBottom: 10,
            display: "inline-flex", alignItems: "center", gap: 8, textTransform: "uppercase"
          }}>
            <AlertTriangle size={14} aria-hidden="true" /> RISK FLAGS
          </div>
          {risk_flags.map((f, i) => {
            const color = LEVEL_COLORS[f.level] || "var(--au-accent-ink)";
            return (
              <div key={i} style={{
                background: "var(--au-glass)",
                borderLeft: "3px solid " + color,
                borderRadius: "var(--au-r-input)",
                padding: "8px 12px",
                marginBottom: 6,
                fontSize: 13,
                color: "var(--au-text)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}>
                <span>{f.msg}</span>
                <span className="au-chip au-chip-status" style={{ color }}>
                  <span className="au-led" style={{ background: color, boxShadow: `0 0 6px ${color}` }} />
                  {f.level.toUpperCase()}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function InfoCell({ icon: Icon, label, value }) {
  return (
    <div>
      <div style={{
        fontSize: 10, color: "var(--au-muted)", letterSpacing: 1, marginBottom: 4,
        display: "inline-flex", alignItems: "center", gap: 6
      }}>
        {Icon && <Icon size={12} aria-hidden="true" />} {label.toUpperCase()}
      </div>
      <div style={{ color: "var(--au-text)", fontSize: 13, fontWeight: 500, wordBreak: "break-word" }}>{value}</div>
    </div>
  );
}

function ScoreCell({ label, value, color }) {
  return (
    <div style={{
      background: "var(--au-glass)",
      border: "1px solid var(--au-hair)",
      borderRadius: "var(--au-r-sm)",
      padding: "10px 14px"
    }}>
      <div style={{ fontSize: 10, color: "var(--au-muted)", letterSpacing: 1, marginBottom: 4 }}>{label}</div>
      <div className="au-mono" style={{ color: color, fontSize: 16, fontWeight: 700 }}>{value}</div>
    </div>
  );
}
